import axios from "axios";
import {
  SupplierFormData,
  SupplierListResponse,
  SupplierQueryParams,
  SupplierSingleResponse,
  SuppliersPagination,
} from "@/types/supplier";

const API_BASE_URL = import.meta.env.VITE_EXTENS_API_URL;

const getToken = () => {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
};

const getHeaders = () => {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

interface ApiBodyPayload {
  success?: boolean;
  message?: string;
  data?: unknown;
  pagination?: SuppliersPagination;
}

interface ApiRawResponse {
  body?: string | ApiBodyPayload;
  success?: boolean;
  message?: string;
  data?: unknown;
  pagination?: SuppliersPagination;
}

const unwrapResponsePayload = (rawData: ApiRawResponse): ApiBodyPayload => {
  if (rawData?.body) {
    return typeof rawData.body === "string"
      ? (JSON.parse(rawData.body) as ApiBodyPayload)
      : rawData.body;
  }
  return rawData as ApiBodyPayload;
};

const resolveErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as
      | { body?: { message?: string } | string; message?: string }
      | undefined;

    if (typeof errorData?.body === "string") {
      try {
        const parsed = JSON.parse(errorData.body) as { message?: string };
        if (parsed?.message) return parsed.message;
      } catch (_) {
        // Ignore JSON parse error and continue with other fallbacks.
      }
    }

    if (
      errorData?.body &&
      typeof errorData.body === "object" &&
      "message" in errorData.body &&
      typeof errorData.body.message === "string"
    ) {
      return errorData.body.message;
    }

    if (typeof errorData?.message === "string") {
      return errorData.message;
    }
  }

  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const defaultPagination: SuppliersPagination = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
};

export const supplierService = {
  list: async (params: SupplierQueryParams): Promise<SupplierListResponse> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/suppliers`, {
        params,
        headers: getHeaders(),
      });

      const payload = unwrapResponsePayload(response.data);

      if (payload?.success === false) {
        throw new Error(payload?.message || "No se pudieron obtener los proveedores");
      }

      return {
        success: true,
        data: Array.isArray(payload?.data) ? payload.data : [],
        pagination: payload?.pagination || defaultPagination,
      };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudieron obtener los proveedores"));
    }
  },

  getById: async (
    id: string,
    params?: { slug?: string },
  ): Promise<SupplierSingleResponse> => {
    try {
      const response = await axios.get(`${API_BASE_URL}/suppliers/${id}`, {
        params,
        headers: getHeaders(),
      });

      const payload = unwrapResponsePayload(response.data);

      if (payload?.success === false) {
        throw new Error(payload?.message || "No se pudo obtener el proveedor");
      }

      return {
        success: true,
        data: payload?.data as any,
        message: payload?.message,
      };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudo obtener el proveedor"));
    }
  },

  create: async (
    payload: SupplierFormData & { slug?: string },
  ): Promise<SupplierSingleResponse> => {
    try {
      const response = await axios.post(`${API_BASE_URL}/suppliers`, payload, {
        headers: getHeaders(),
      });

      const parsedPayload = unwrapResponsePayload(response.data);

      if (parsedPayload?.success === false) {
        throw new Error(parsedPayload?.message || "No se pudo crear el proveedor");
      }

      return {
        success: true,
        data: parsedPayload?.data as any,
        message: parsedPayload?.message,
      };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudo crear el proveedor"));
    }
  },

  update: async (
    id: string,
    payload: Partial<SupplierFormData> & { slug?: string },
  ): Promise<SupplierSingleResponse> => {
    try {
      const response = await axios.put(`${API_BASE_URL}/suppliers/${id}`, payload, {
        headers: getHeaders(),
      });

      const parsedPayload = unwrapResponsePayload(response.data);

      if (parsedPayload?.success === false) {
        throw new Error(parsedPayload?.message || "No se pudo actualizar el proveedor");
      }

      return {
        success: true,
        data: parsedPayload?.data as any,
        message: parsedPayload?.message,
      };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudo actualizar el proveedor"));
    }
  },

  remove: async (
    id: string,
    payload?: { slug?: string },
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/suppliers/${id}`, {
        data: payload,
        headers: getHeaders(),
      });

      const parsedPayload = unwrapResponsePayload(response.data);

      if (parsedPayload?.success === false) {
        throw new Error(parsedPayload?.message || "No se pudo eliminar el proveedor");
      }

      return {
        success: true,
        message: parsedPayload?.message,
      };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudo eliminar el proveedor"));
    }
  },

  getUploadUrl: async (
    id: string,
    fileName: string,
    contentType: string,
    context: { slug?: string },
  ): Promise<{ success: boolean; data?: { upload_url: string; file_url: string } }> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/suppliers/${id}/rtu-upload-url`,
        { fileName, contentType },
        {
          params: context,
          headers: getHeaders(),
        }
      );

      const parsedPayload = unwrapResponsePayload(response.data);

      if (parsedPayload?.success === false) {
        throw new Error(parsedPayload?.message || "No se pudo obtener la URL de subida");
      }

      return {
        success: true,
        data: parsedPayload?.data as any,
      };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudo obtener la URL de subida"));
    }
  },

  uploadToS3: async (
    uploadUrl: string,
    file: File,
  ): Promise<{ success: boolean }> => {
    try {
      await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      return { success: true };
    } catch (error: unknown) {
      throw new Error(resolveErrorMessage(error, "No se pudo subir el archivo a S3"));
    }
  },
};
